import { Box, Button, InputBase, Tab, Tabs } from '@material-ui/core';
import { useCallback, useContext, useEffect, useState } from 'react';

import { Typography, Accordion } from '@components';
import { content } from '@content';

import { DisclosureText } from './DisclosureText';

import { EditMode } from '@components/Wysiwyg/EditMode';
import { TopBarContext } from '@layouts';
import { ApiError, experience, global, modules } from '@modules';
import { useAppDispatch } from '@store';
import { useStyles } from './ModuleSettings.styles';
import { ModuleSettingsProps } from './ModuleSettings.props';

/**
 ModuleSettings component
 @returns {JSX.Element}
 */

export const ModuleSettings: React.FC<ModuleSettingsProps> = ({
  module,
  onEditorChange,
  isModuleChanged,
  onModuleClose,
  onModuleSave,
}): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const [topBarCtx, setTopBarCtx] = useContext(TopBarContext);
  const [tab, setTab] = useState(0);
  const [isEditTitleModeOn, setIsEditTitleModeOn] = useState(false);
  const [panelTitleValue, setPanelTitleValue] = useState(module.templateName || module.name || '');
  const handleChangeTab = useCallback((event: React.ChangeEvent<unknown>, value: number) => setTab(value), []);
  const [expanded, setExpanded] = useState(false);
  const [disclosureRequired, setDisclosureRequired] = useState(false);
  const [disclosuresAccordionTitle, setDisclosuresAccordionTitle] = useState(content.disclosures);

  const toggleAccordion = useCallback(() => setExpanded((prevState) => !prevState), [setExpanded]);

  const handleTriggerPanelTitle = () => {
    setIsEditTitleModeOn((prev) => !prev);
  };

  const cancelEditPanelTitle = () => {
    handleTriggerPanelTitle();
    setPanelTitleValue(module.templateName || module.name || '');
  };

  const handlePanelTitleValueChange = (e: any) => {
    setPanelTitleValue(e.target.value);
  };

  const handleModuleRename = async () => {
    const resp = await dispatch(
      modules.thunk.renameModuleTemplate({
        module: module.id,
        templateName: panelTitleValue,
      }),
    );

    if (resp.meta.requestStatus === 'rejected') {
      await dispatch(
        global.actions.enqueueNotification({
          message: 'To rename the content module, please, provide a unique name across the Business Unit',
          options: { variant: 'warning' },
        }),
      );
      handleTriggerPanelTitle();

      return;
    }

    await dispatch(experience.actions.updateModule(resp.payload as any));

    setTopBarCtx((prev) => ({
      ...prev,
      contentModuleName: panelTitleValue,
      chosenTreatment: {
        ...prev.chosenTreatment,
        moduleTemplateName: panelTitleValue,
      } as any,
    }));

    handleTriggerPanelTitle();
  };

  useEffect(() => {
    setPanelTitleValue(module.templateName || module.name || '');
  }, [module]);

  useEffect(() => {
    if (disclosureRequired) {
      setDisclosuresAccordionTitle(`${content.disclosures} (Required)`);
    } else {
      setDisclosuresAccordionTitle(`${content.disclosures}`);
    }
  }, [disclosureRequired]);

  return (
    <Box className={styles.moduleSettings}>
      {/* <Box className={styles.header}>
        <Box className={styles.titleWrapper}>
          {isEditTitleModeOn ? (
            <>
              <Typography.SuperSmallCaption>{content.module}</Typography.SuperSmallCaption>
              <Box className={styles.renameBar}>
                <InputBase
                  className={styles.renameField}
                  // onDoubleClick={handleTriggerPanelTitle}
                  value={panelTitleValue}
                  onChange={handlePanelTitleValueChange}
                />
                <Box className={styles.renameBarButtons}>
                  <Button
                    className={styles.editModule}
                    variant="outlined"
                    color="primary"
                    onClick={cancelEditPanelTitle}
                  >
                    {content.cancel}
                  </Button>
                  <Button
                    className={styles.editModule}
                    variant="contained"
                    color="secondary"
                    onClick={handleModuleRename}
                  >
                    {content.save}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box onDoubleClick={handleTriggerPanelTitle}>
              <Typography.SuperSmallCaption className={styles.panelTitleHeading}>
                {content.module}
              </Typography.SuperSmallCaption>
              <Typography.Title className={styles.title}>{module.templateName || module.name || ''}</Typography.Title>
            </Box>
          )}
        </Box>
      </Box> */}
      <Box className={styles.panel}>
        <Box className={styles.header}>
          <Box className={styles.titleWrapper}>
            {isEditTitleModeOn ? (
              <>
                <Typography.SuperSmallCaption>{content.module}</Typography.SuperSmallCaption>
                <Box className={styles.renameBar}>
                  <InputBase
                    className={styles.renameField}
                    // onDoubleClick={handleTriggerPanelTitle}
                    value={panelTitleValue}
                    onChange={handlePanelTitleValueChange}
                  />
                  <Box className={styles.renameBarButtons}>
                    <Button
                      className={styles.editModule}
                      variant="outlined"
                      color="primary"
                      onClick={cancelEditPanelTitle}
                    >
                      {content.cancel}
                    </Button>
                    <Button
                      className={styles.editModule}
                      variant="contained"
                      color="secondary"
                      onClick={handleModuleRename}
                    >
                      {content.save}
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box onDoubleClick={handleTriggerPanelTitle}>
                <Typography.SuperSmallCaption className={styles.panelTitleHeading}>
                  {content.module}
                </Typography.SuperSmallCaption>
                <Typography.Body className={styles.elementName}>
                  {module.templateName || module.name || ''}
                </Typography.Body>
              </Box>
            )}
          </Box>
        </Box>
        <Accordion expanded={expanded} onChange={toggleAccordion} title={disclosuresAccordionTitle} uiType="quaternary">
          <Box className={styles.content}>
            <DisclosureText module={module} setDisclosureRequired={setDisclosureRequired} />
          </Box>
        </Accordion>
        <Box className={styles.buttonContainer}>
          <Button className={styles.close} color="primary" variant="outlined" size="medium" onClick={onModuleClose}>
            {content.close}
          </Button>
          <Button
            className={styles.save}
            variant="contained"
            color="secondary"
            size="medium"
            disabled={!isModuleChanged}
            onClick={isModuleChanged ? onModuleSave : undefined}
          >
            {content.save}
          </Button>
        </Box>
      </Box>
      {/* <Box className={styles.editModeContainer}>
        <EditMode />
      </Box> */}
    </Box>
  );
};
